import { Clock, ExternalLink, Link2 } from "lucide-react";

import type { PublicSessionNote } from "@/lib/public-notes";
import { toPlayerClock } from "@/lib/recording-clock";
import { formatTimecode } from "@/lib/timecode";
import { cn } from "@/lib/utils";
import { segment } from "../../../../../APITypes/Segment.cs";
import type { PlayAtTarget } from "./usePlayAt";

function linkHost(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export default function SessionNotesList({
  notes,
  segments,
  playsHighlightsStream,
  sessionIndex,
  playAt,
}: {
  notes: PublicSessionNote[];
  segments: segment[];
  /** True when the player is on the trimmed highlights-only stream. */
  playsHighlightsStream: boolean;
  sessionIndex: number;
  playAt: (target: PlayAtTarget) => void;
}) {
  // Rendered under the session's highlights, and only when it has notes.
  if (!notes.length) return null;

  return (
    <div className="pb-4">
      <ul className="flex flex-col gap-2">
        {notes.map((note) => {
          const body = note.text ? (
            // Author-written text: never parse it as HTML.
            <p className="whitespace-pre-line break-words text-left text-xs leading-relaxed">
              {note.text}
            </p>
          ) : null;

          if (note.kind === "moment" && note.atMs !== undefined) {
            const target = toPlayerClock(
              note.atMs,
              segments,
              playsHighlightsStream
            );
            return (
              <li key={note.id}>
                <button
                  type="button"
                  disabled={target === null}
                  title={
                    target === null
                      ? "Not in the highlights. Switch to the full recording to watch it."
                      : `Play from ${formatTimecode(note.atMs)}`
                  }
                  onClick={() => {
                    if (target !== null) {
                      playAt({ index: sessionIndex, playerMs: target });
                    }
                  }}
                  className={cn(
                    "w-full rounded-md border border-transparent bg-white/10 p-2 dark:bg-zinc-950",
                    target === null
                      ? "cursor-default opacity-50"
                      : "hover:bg-purple-500/5 hover:opacity-90"
                  )}
                >
                  <span className="mb-1 flex items-center gap-1 text-xs font-medium text-accent">
                    <Clock size={12} aria-hidden="true" />
                    {formatTimecode(note.atMs)}
                  </span>
                  {body}
                </button>
              </li>
            );
          }

          if (note.kind === "link" && note.url) {
            const host = linkHost(note.url);
            return (
              <li key={note.id}>
                <a
                  href={note.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-start gap-2 rounded-md border border-transparent bg-white/10 p-2 text-xs hover:bg-purple-500/5 dark:bg-zinc-950"
                >
                  <Link2
                    size={13}
                    className="mt-0.5 shrink-0 text-accent"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {note.text || host}
                    </span>
                    {note.text ? (
                      <span className="block truncate opacity-60">{host}</span>
                    ) : null}
                  </span>
                  <ExternalLink
                    size={12}
                    className="mt-0.5 shrink-0 opacity-60"
                    aria-hidden="true"
                  />
                </a>
              </li>
            );
          }

          return body ? (
            <li
              key={note.id}
              className="rounded-md bg-white/10 p-2 dark:bg-zinc-950"
            >
              {body}
            </li>
          ) : null;
        })}
      </ul>
    </div>
  );
}
